import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, Minus, Plus, Trash2, Clock, ArrowLeft, CheckCircle, 
  Loader2, CreditCard, Smartphone, Building, ChevronDown, ChevronUp,
  Sparkles, Coffee, Timer, Bell, Edit2, X, BellRing, Mail, Star, Tag, Users,
  Shield, Leaf, RefreshCw, RotateCcw, AlertCircle, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { usePushNotifications } from '@/context/PushNotificationContext';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import CoffeeClubCartUpsell from '@/components/ordering/CoffeeClubCartUpsell';
import OnlineCoffeePassRedemption from '@/components/ordering/OnlineCoffeePassRedemption';

import { CoffeePassOrderUpsell } from '@/components/CoffeePassPromo';
import LocationDisclaimer from '@/components/LocationDisclaimer';

import api from '@/utils/apiClient';

// Trust badges data
const TRUST_BADGES = [
  { icon: Shield, label: "Secure Checkout", color: "text-green-600 dark:text-green-400" },
  { icon: Leaf, label: "Fresh Guarantee", color: "text-emerald-600 dark:text-emerald-400" },
  { icon: Star, label: "4.9★ Rated", color: "text-amber-500" },
];

// Swipeable Cart Item Component for mobile - Completely redesigned for narrow screens
function SwipeableCartItem({ item, index, customDetails, getItemDescription, removeFromCart, updateQuantity, editingNotes, setEditingNotes, tempNotes, setTempNotes, updateSpecialInstructions }) {
  const x = useMotionValue(0);
  const controls = useAnimation();
  const [isDragging, setIsDragging] = useState(false);
  
  // Background color based on swipe distance
  const background = useTransform(
    x, 
    [-150, -100, 0], 
    ['rgb(239, 68, 68)', 'rgb(239, 68, 68)', 'transparent']
  );
  
  // Delete icon opacity
  const deleteOpacity = useTransform(x, [-150, -80, 0], [1, 1, 0]);
  
  const handleDragEnd = (_, info) => {
    setIsDragging(false);
    if (info.offset.x < -100) {
      controls.start({ x: -400, opacity: 0, transition: { duration: 0.3 } }).then(() => {
        removeFromCart(item.cart_key);
      });
    } else {
      controls.start({ x: 0, transition: { type: 'spring', stiffness: 500, damping: 30 } });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -300, transition: { duration: 0.2 } }}
      transition={{ delay: index * 0.05 }}
      className="relative overflow-hidden rounded-2xl"
      data-testid={`cart-item-${item.cart_key}`}
    >
      {/* Delete Background (revealed on swipe) - Mobile only */}
      <motion.div 
        className="absolute inset-0 flex items-center justify-end pr-6 sm:hidden rounded-2xl"
        style={{ background }}
      >
        <motion.div style={{ opacity: deleteOpacity }}>
          <Trash2 className="w-6 h-6 text-white" />
        </motion.div>
      </motion.div>
      
      {/* Swipeable Content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -150, right: 0 }}
        dragElastic={0.1}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        animate={controls}
        style={{ x }}
        className="bg-card p-3 sm:p-4 rounded-2xl shadow-sm border border-border relative touch-pan-y"
      >
        {/* ===== MOBILE LAYOUT (< 640px) ===== */}
        <div className="sm:hidden">
          {/* Row 1: Image + Name + Delete */}
          <div className="flex items-start gap-3 mb-3">
            <img 
              src={item.image} 
              alt={item.name}
              className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium text-foreground text-sm leading-tight">{item.name}</h3>
                <button
                  onClick={() => removeFromCart(item.cart_key)}
                  className="w-7 h-7 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0"
                  data-testid={`remove-item-mobile-${item.cart_key}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {getItemDescription(item) && (
                <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
                  {getItemDescription(item)}
                </p>
              )}
            </div>
          </div>
          
          {/* Row 2: Customizations (if any) - Full width, horizontal scroll */}
          {customDetails.length > 0 && (
            <div className="mb-3 space-y-1.5">
              {customDetails.map((detail) => (
                <div key={`${detail.type}-${detail.label}`} className="flex items-start gap-2">
                  <span className={`text-[11px] font-bold uppercase flex-shrink-0 pt-0.5 ${
                    detail.type === 'removed' ? 'text-red-500' :
                    detail.type === 'extra' ? 'text-green-500' :
                    'text-blue-500'
                  }`}>
                    {detail.label}:
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {detail.items.map((itemText) => (
                      <span 
                        key={`${detail.type}-${itemText}`}
                        className={`text-[11px] px-1.5 py-0.5 rounded ${
                          detail.type === 'removed' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                          detail.type === 'extra' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}
                      >
                        {itemText}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Row 3: Price + Quantity (side by side) */}
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <p className="text-primary font-bold text-base">{item.price.toFixed(2)}</p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => updateQuantity(item.cart_key, item.quantity - 1)}
                className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"
                data-testid={`decrease-qty-mobile-${item.cart_key}`}
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-6 text-center font-medium text-sm">{item.quantity}</span>
              <button
                onClick={() => updateQuantity(item.cart_key, item.quantity + 1)}
                className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"
                data-testid={`increase-qty-mobile-${item.cart_key}`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          {/* Row 4: Add notes (optional, collapsible) */}
          <div className="mt-2 pt-2 border-t border-border/30">
            {editingNotes === item.cart_key ? (
              <div className="flex gap-1.5">
                <Input
                  value={tempNotes}
                  onChange={(e) => setTempNotes(e.target.value)}
                  placeholder="Special instructions..."
                  className="text-xs h-7 flex-1"
                  data-testid={`notes-input-${item.cart_key}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    updateSpecialInstructions(item.cart_key, tempNotes);
                    setEditingNotes(null);
                  }}
                  data-testid={`save-notes-${item.cart_key}`}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => setEditingNotes(null)}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setEditingNotes(item.cart_key);
                  setTempNotes(item.special_instructions || '');
                }}
                className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1"
                data-testid={`edit-notes-${item.cart_key}`}
              >
                <Edit2 className="w-3 h-3" />
                <span className="truncate">{item.special_instructions || 'Add notes'}</span>
              </button>
            )}
          </div>
        </div>
        
        {/* ===== DESKTOP LAYOUT (>= 640px) ===== */}
        <div className="hidden sm:block">
          <div className="flex gap-4">
            <img 
              src={item.image} 
              alt={item.name}
              className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground">{item.name}</h3>
              {getItemDescription(item) && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {getItemDescription(item)}
                </p>
              )}
              
              {/* Customization Details */}
              {customDetails.length > 0 && (
                <div className="mt-2 space-y-1">
                  {customDetails.map((detail) => (
                    <div key={`${detail.type}-${detail.label}`} className="flex items-center gap-2">
                      <span className={`text-xs font-semibold uppercase ${
                        detail.type === 'removed' ? 'text-red-600 dark:text-red-400' :
                        detail.type === 'extra' ? 'text-green-600 dark:text-green-400' :
                        'text-blue-600 dark:text-blue-400'
                      }`}>
                        {detail.label}:
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {detail.items.map((itemText) => (
                          <span 
                            key={`${detail.type}-${itemText}`}
                            className={`text-xs px-2 py-0.5 rounded-lg ${
                              detail.type === 'removed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                              detail.type === 'extra' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                              'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            }`}
                          >
                            {itemText}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-primary font-semibold mt-2">{item.price.toFixed(2)}</p>
              
              {/* Special Instructions */}
              {editingNotes === item.cart_key ? (
                <div className="mt-2 flex gap-2">
                  <Input
                    value={tempNotes}
                    onChange={(e) => setTempNotes(e.target.value)}
                    placeholder="Add special instructions..."
                    className="text-sm h-8"
                    data-testid={`notes-input-desktop-${item.cart_key}`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      updateSpecialInstructions(item.cart_key, tempNotes);
                      setEditingNotes(null);
                    }}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingNotes(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setEditingNotes(item.cart_key);
                    setTempNotes(item.special_instructions || '');
                  }}
                  className="mt-1 text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                  data-testid={`edit-notes-desktop-${item.cart_key}`}
                >
                  <Edit2 className="w-3 h-3" />
                  {item.special_instructions || 'Add notes'}
                </button>
              )}
            </div>
            
            {/* Desktop sidebar: delete + quantity */}
            <div className="flex flex-col items-end justify-between flex-shrink-0">
              <button
                onClick={() => removeFromCart(item.cart_key)}
                className="w-8 h-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20"
                data-testid={`remove-item-${item.cart_key}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateQuantity(item.cart_key, item.quantity - 1)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                  data-testid={`decrease-qty-${item.cart_key}`}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium">{item.quantity}</span>
                <button
                  onClick={() => updateQuantity(item.cart_key, item.quantity + 1)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                  data-testid={`increase-qty-${item.cart_key}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function OrderPage() {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, clearCart, cartTotal, updateSpecialInstructions, addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { isSupported: pushSupported, isSubscribed: isPushSubscribed, subscribe: subscribePush, permission: pushPermission } = usePushNotifications();
  const [step, setStep] = useState('cart');
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [waitTime, setWaitTime] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [tempNotes, setTempNotes] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyTier, setLoyaltyTier] = useState(null);
  
  // Previous orders for reorder feature
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loadingPreviousOrders, setLoadingPreviousOrders] = useState(false);
  
  // Partner code state
  const [partnerCode, setPartnerCode] = useState('');
  const [partnerCodeValid, setPartnerCodeValid] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: user?.phone || '',
    pickup_time: '',
    payment_method: 'mpaisa_qr',  // M-PAiSA QR is default (static QR code)
    mpaisa_ref_code: '',  // For QR code verification
    sms_notifications: true,   // SMS notifications enabled by default
    email_notifications: true  // Email notifications enabled by default
  });

  // Redirect to menu if cart is empty (better UX for QR code scanning)
  useEffect(() => {
    if (cart.length === 0 && step === 'cart') {
      navigate('/menu', { replace: true });
    }
  }, [cart.length, step, navigate]);

  // Fetch wait time and settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [waitRes, settingsRes] = await Promise.all([
          api.get('/wait-time'),
          api.get('/settings')
        ]);
        setWaitTime(waitRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, []);

  // Fetch previous orders for reorder feature (authenticated users only)
  useEffect(() => {
    const fetchPreviousOrders = async () => {
      if (!isAuthenticated || !user?.id) return;
      setLoadingPreviousOrders(true);
      try {
        const res = await api.get(`/orders/user/${user.id}?limit=3`);
        setPreviousOrders(res.data.orders || []);
      } catch (err) {
        console.error('Failed to fetch previous orders:', err);
      }
      setLoadingPreviousOrders(false);
    };
    fetchPreviousOrders();
  }, [isAuthenticated, user?.id]);

  // Handle reorder - add all items from a previous order to cart
  const handleReorder = (order) => {
    if (!order.items || order.items.length === 0) return;
    
    order.items.forEach(item => {
      addToCart(
        {
          id: item.item_id,
          name: item.name,
          price: item.price,
          image: item.image || null,
          category: item.category || null
        },
        item.selected_size || null,
        item.modifiers || null,
        item.special_instructions || null,
        null, // removed_ingredients not stored in order
        null, // extra_ingredients not stored in order
        null  // add_ons not stored in order
      );
    });
    
    toast.success(`${order.items.length} items added from your previous order!`);
  };

  // Fetch customer discount (founding member or tier-based)
  useEffect(() => {
    const fetchCustomerDiscount = async () => {
      if (!isAuthenticated || !user?.id) return;
      try {
        const res = await api.get(`/customer-discount?user_id=${user.id}&email=${encodeURIComponent(user.email || '')}`);
        setLoyaltyDiscount(res.data.discount || 0);
        if (res.data.tier) {
          setLoyaltyTier({ name: res.data.tier, discount: res.data.discount });
        } else if (res.data.source === 'founding_member') {
          setLoyaltyTier({ name: 'Founding Member', discount: res.data.discount, isFoundingMember: true });
        }
      } catch (err) {
        console.error('Failed to fetch customer discount:', err);
      }
    };
    fetchCustomerDiscount();
  }, [isAuthenticated, user?.id, user?.email]);

  // Validate partner code
  const validatePartnerCode = useCallback(async (code) => {
    if (!code || code.length < 4) {
      setPartnerCodeValid(null);
      setPartnerInfo(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      // Use the new commission-based referral partners endpoint (unified contacts collection)
      const res = await api.get(`/referral-partners/validate/${encodeURIComponent(code)}`);
      
      if (res.data.valid) {
        setPartnerCodeValid(true);
        setPartnerInfo(res.data);
      } else {
        setPartnerCodeValid(false);
        setPartnerInfo(null);
      }
    } catch (err) {
      // Fallback: Check legacy partners collection
      try {
        const legacyRes = await api.get(`/partners/validate/${encodeURIComponent(code)}`);
        if (legacyRes.data.valid) {
          setPartnerCodeValid(true);
          setPartnerInfo(legacyRes.data);
        } else {
          setPartnerCodeValid(false);
          setPartnerInfo(null);
        }
      } catch {
        setPartnerCodeValid(false);
        setPartnerInfo(null);
      }
    }
    setValidatingCode(false);
  }, []);

  // Debounce partner code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (partnerCode) {
        validatePartnerCode(partnerCode);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [partnerCode, validatePartnerCode]);

  // Fetch upsells based on cart
  useEffect(() => {
    const fetchUpsells = async () => {
      if (cart.length === 0) return;
      try {
        const itemIds = cart.map(i => i.item_id).join(',');
        const res = await api.get(`/upsells/cart?item_ids=${itemIds}`);
        setUpsells(res.data.suggestions || []);
      } catch (err) {
        console.error('Failed to fetch upsells:', err);
      }
    };
    fetchUpsells();
  }, [cart]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          selected_size: item.selected_size || null,
          modifiers: item.modifiers || [],
          special_instructions: item.special_instructions || null,
          base_price: item.base_price || item.price,
          category: item.category || null  // For 3-station printer routing
        })),
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        pickup_time: formData.pickup_time || null,
        user_id: isAuthenticated ? user.id : null,
        payment_method: formData.payment_method,
        order_type: 'online',
        sms_notifications_enabled: formData.sms_notifications,
        email_notifications_enabled: formData.email_notifications,
        partner_code: partnerCodeValid ? partnerCode : null  // Include partner code if valid
      };

      const res = await api.post('/orders', orderData);

      const order = res.data;
      
      // If M-PAiSA QR payment selected, verify the reference code and confirm order
      if (formData.payment_method === 'mpaisa_qr') {
        // Reference code will be verified at backend during order processing
        // For now, just include it in the order and show confirmation
        if (!formData.mpaisa_ref_code || formData.mpaisa_ref_code.length < 6) {
          setLoading(false);
          toast.error('Please enter a valid M-PAiSA confirmation code (at least 6 digits)');
          return;
        }
        
        // Store the ref code with the order
        await api.post(`/orders/${order.id}/payment-ref`, {
          payment_method: 'mpaisa_qr',
          ref_code: formData.mpaisa_ref_code
        }).catch(() => {}); // Non-blocking
        
        // Clear cart and show confirmation
        clearCart();
        setOrderDetails(order);
        setStep('confirmation');
        setLoading(false);
        return;
      }
      
      // If M-PAiSA Link payment selected, redirect to payment gateway
      if (formData.payment_method === 'mpaisa') {
        try {
          // Create M-PAiSA payment request
          const paymentRes = await api.post('/payments/mpaisa/create', {
            order_id: order.id,
            amount: finalTotal,
            description: `Bulaccino Cafe Order #${order.order_number || order.id}`,
            customer_phone: formData.customer_phone,
            customer_name: formData.customer_name,
            source: 'web'
          });
          
          if (paymentRes.data.success && paymentRes.data.payment_url) {
            // Store order details for after payment
            localStorage.setItem('pendingOrder', JSON.stringify(order));
            clearCart();
            // Redirect to M-PAiSA payment page
            window.location.href = paymentRes.data.payment_url;
            return;
          } else {
            throw new Error(paymentRes.data.error || 'Failed to create payment');
          }
        } catch (paymentError) {
          console.error('M-PAiSA payment error:', paymentError);
          toast.error('Payment setup failed. Please try again.');
          setLoading(false);
          return;
        }
      }
      
      // For other payment methods, show confirmation
      setOrderDetails(order);
      clearCart();
      setStep('confirmation');
      toast.success('Order placed successfully!');
      
      // Subscribe to push notifications for this order if enabled
      if (pushEnabled || isPushSubscribed) {
        subscribePush(user?.id, order.id);
      }
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
    }

    setLoading(false);
  };

  // Calculate discount amount and final total
  // IMPORTANT: Bula Coffee Pass items (free coffee & upgrades) are NOT eligible for tier discounts
  // Check for BOTH isCoffeeClubRedemption AND isCoffeePassRedemption (modal uses isCoffeePassRedemption)
  const discountableItems = cart.filter(item => !item.isCoffeeClubRedemption && !item.isCoffeePassRedemption);
  const coffeePassItems = cart.filter(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);
  const discountableSubtotal = discountableItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const coffeePassSubtotal = coffeePassItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Check if cart has Bula Coffee Pass annual pass
  // Check by type OR by name (case-insensitive) to catch all sources
  const hasCoffeeClubPass = cart.some(item => 
    item.type === 'coffee_club_pass' || 
    (item.name && item.name.toLowerCase().includes('bula coffee pass')) ||
    (item.name && item.name.toLowerCase().includes('coffee pass'))
  );
  
  // VAT is already included in $95 Coffee Pass price
  const coffeePassCount = cart.filter(item => 
    item.type === 'coffee_club_pass' || 
    (item.name && item.name.toLowerCase().includes('bula coffee pass')) ||
    (item.name && item.name.toLowerCase().includes('coffee pass'))
  ).reduce((sum, item) => sum + (item.quantity || 1), 0);
  const coffeePassVAT = 0; // VAT already included in $95 price
  
  // Check if cart ONLY contains Coffee Pass (no items that need to be made)
  const cartOnlyCoffeePass = cart.length > 0 && cart.every(item => 
    item.type === 'coffee_club_pass' || 
    (item.name && item.name.toLowerCase().includes('bula coffee pass')) ||
    (item.name && item.name.toLowerCase().includes('coffee pass'))
  );
  
  // Partner code (5%) only applies if user has no loyalty discount
  const partnerDiscount = (partnerCodeValid && loyaltyDiscount === 0) ? 5 : 0;
  const effectiveDiscount = loyaltyDiscount > 0 ? loyaltyDiscount : partnerDiscount;
  // Discount applies ONLY to discountable items (not Coffee Pass redemptions)
  const discountAmount = effectiveDiscount > 0 ? discountableSubtotal * (effectiveDiscount / 100) : 0;
  const finalTotal = discountableSubtotal - discountAmount + coffeePassSubtotal + coffeePassVAT;

  // Format cart item description
  const getItemDescription = (item) => {
    const parts = [];
    if (item.selected_size) parts.push(item.selected_size);
    if (item.modifiers?.length > 0) {
      item.modifiers.forEach(m => parts.push(m.selected_option || m.modifier_name));
    }
    return parts.join(' • ');
  };

  // Get full customization details for cart display
  const getCustomizationDetails = (item) => {
    const details = [];
    
    // Removed ingredients
    if (item.removed_ingredients?.length > 0) {
      details.push({
        type: 'removed',
        label: 'No',
        items: item.removed_ingredients
      });
    }
    
    // Extra ingredients
    if (item.extra_ingredients?.length > 0) {
      details.push({
        type: 'extra',
        label: 'Extra',
        items: item.extra_ingredients.map(e => `${e.name} (+${e.price?.toFixed(2) || '0.00'})`)
      });
    }
    
    // Add-ons
    if (item.add_ons?.length > 0) {
      details.push({
        type: 'addon',
        label: 'Add',
        items: item.add_ons.map(a => `${a.name} (+${a.price?.toFixed(2) || '0.00'})`)
      });
    }
    
    return details;
  };

  // Cart View
  if (step === 'cart') {
    return (
      <div className="pt-24 pb-16 min-h-screen" data-testid="order-page">
        <SEO {...SEO_CONFIG.order} path="/order" />
        
        <section className="py-8 bg-gradient-to-b from-secondary/50 to-transparent">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-heading text-4xl md:text-5xl text-foreground mb-2"
            >
              Your Order
            </motion.h1>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-muted-foreground">
                <p>Review your items and proceed to checkout</p>
                {/* Only show wait time if cart has items that need to be made (not just Coffee Pass) */}
                {waitTime && !cartOnlyCoffeePass && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                    <Timer className="w-4 h-4" />
                    ~{waitTime.minutes} min wait
                  </span>
                )}
              </div>
              {isAuthenticated && (
                <Link 
                  to="/order-history" 
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                  data-testid="order-history-link"
                >
                  <Clock className="w-4 h-4" />
                  Order History
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="py-8">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            {cart.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h2 className="font-heading text-2xl mb-2">Your cart is empty</h2>
                <p className="text-muted-foreground mb-6">Add some delicious items from our menu!</p>
                
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                  <Link to="/menu" data-testid="empty-cart-menu-btn">
                    <Button className="rounded-full bg-primary hover:bg-primary/90">
                      Browse Menu
                    </Button>
                  </Link>
                </div>
                
                {/* Reorder Section - for authenticated users with previous orders */}
                {isAuthenticated && previousOrders.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-8 pt-8 border-t border-border"
                  >
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <RotateCcw className="w-5 h-5 text-primary" />
                      <h3 className="font-heading text-lg">Order Again</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Quickly reorder from your recent orders
                    </p>
                    <div className="grid gap-3 max-w-md mx-auto">
                      {previousOrders.slice(0, 3).map((order) => (
                        <motion.button
                          key={order.id}
                          onClick={() => handleReorder(order)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex items-center gap-4 p-4 bg-card rounded-2xl border border-border hover:border-primary/50 transition-all text-left"
                          data-testid={`reorder-btn-${order.id}`}
                        >
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <RefreshCw className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm">
                              {order.items?.length || 0} items • ${order.total?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {order.items?.slice(0, 2).map(i => i.name).join(', ')}
                              {order.items?.length > 2 && ` +${order.items.length - 2} more`}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Plus className="w-5 h-5 text-primary" />
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-4">
                  {/* Cart Items with Swipe-to-Delete */}
                  <AnimatePresence mode="popLayout">
                    {cart.map((item, i) => {
                      const customDetails = getCustomizationDetails(item);
                      return (
                        <SwipeableCartItem
                          key={item.cart_key}
                          item={item}
                          index={i}
                          customDetails={customDetails}
                          getItemDescription={getItemDescription}
                          removeFromCart={removeFromCart}
                          updateQuantity={updateQuantity}
                          editingNotes={editingNotes}
                          setEditingNotes={setEditingNotes}
                          tempNotes={tempNotes}
                          setTempNotes={setTempNotes}
                          updateSpecialInstructions={updateSpecialInstructions}
                        />
                      );
                    })}
                  </AnimatePresence>

                  {/* Bula Coffee Pass Redemption - shown if user has active pass */}
                  <OnlineCoffeePassRedemption
                    userEmail={formData.customer_email || user?.email}
                    userPhone={formData.customer_phone || user?.phone}
                    onAddToCart={(item) => {
                      // Add redeemed coffee to cart with all customizations
                      addToCart(
                        {
                          id: item.id || item.item_id,
                          name: item.name,
                          price: item.price,
                          image: item.image,
                          category: item.category || 'coffee'
                        },
                        item.size, // size
                        item.modifiers, // modifiers
                        null, // notes
                        null, // removed ingredients
                        null, // extras
                        null, // add-ons
                        {
                          isCoffeePassRedemption: true,
                          isCoffeeClubRedemption: true,
                          passRedemptionId: item.passRedemptionId,
                          skip_customization: true
                        }
                      );
                    }}
                  />

                  {/* Bula Coffee Pass Upsell - shown if user doesn't have active pass */}
                  <CoffeeClubCartUpsell
                    userEmail={formData.customer_email || user?.email}
                    userPhone={formData.customer_phone || user?.phone}
                    onAddToCart={(item) => {
                      // Add Coffee Pass pass as a special item to cart
                      addToCart(
                        {
                          id: item.id,
                          name: item.name,
                          price: item.price,
                          image: null,
                          category: 'membership'
                        },
                        null, // no size
                        null, // no modifiers
                        null, // no notes
                        null, // no removed ingredients
                        null, // no extras
                        null  // no add-ons
                      );
                    }}
                  />

                  {/* Upsells Section */}
                  {upsells.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-2xl"
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-primary" />
                        <h3 className="font-heading text-lg">Complete Your Order</h3>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {upsells.slice(0, 4).map(item => (
                          <Link 
                            key={item.id} 
                            to="/menu"
                            className="bg-card p-3 rounded-xl hover:shadow-md transition-shadow"
                            data-testid={`upsell-${item.id}`}
                          >
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-full aspect-square rounded-lg object-cover mb-2"
                            />
                            <p className="text-sm font-medium">{item.name}</p>
                            <p className="text-xs text-primary">${item.price.toFixed(2)}</p>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Order Summary Sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-card p-6 rounded-3xl shadow-lg border border-border sticky top-24">
                    <h3 className="font-heading text-xl mb-4">Order Summary</h3>
                    
                    {/* Wait Time - hidden when cart only has Coffee Pass */}
                    {waitTime && !cartOnlyCoffeePass && (
                      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-xl mb-4">
                        <Clock className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium">Estimated Wait</p>
                          <p className="text-xs text-muted-foreground">{waitTime.message}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Subtotal ({cart.reduce((s, i) => s + i.quantity, 0)} items)</span>
                        <span>{cartTotal.toFixed(2)}</span>
                      </div>
                      {effectiveDiscount > 0 && (
                        <div className="flex justify-between text-green-600 dark:text-green-400">
                          <span className="flex items-center gap-1">
                            {loyaltyDiscount > 0 ? (
                              <>
                                <Star className="w-4 h-4" />
                                {loyaltyTier?.name || 'Loyalty'} ({effectiveDiscount}%)
                              </>
                            ) : (
                              <>
                                <Tag className="w-4 h-4" />
                                Partner Code ({effectiveDiscount}%)
                              </>
                            )}
                          </span>
                          <span>-{discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {hasCoffeeClubPass && (
                        <div className="flex justify-between text-muted-foreground text-sm">
                          <span>VAT (12.5% on Coffee Pass)</span>
                          <span>+${coffeePassVAT.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between font-heading text-xl pt-4 border-t border-border">
                      <span>Total</span>
                      <span>{finalTotal.toFixed(2)}</span>
                    </div>
                    <Button 
                      onClick={() => setStep('checkout')}
                      className="w-full mt-6 rounded-full bg-primary hover:bg-primary/90"
                      data-testid="proceed-checkout-btn"
                    >
                      Proceed to Checkout
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  // Checkout View
  if (step === 'checkout') {
    return (
      <div className="pt-24 pb-16 min-h-screen" data-testid="checkout-page">
        <section className="py-8">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <button 
              onClick={() => setStep('cart')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
              data-testid="back-to-cart-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Cart
            </button>

            <h1 className="font-heading text-4xl mb-4">Checkout</h1>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 mb-8 py-4 border-y border-border">
              {TRUST_BADGES.map((badge) => (
                <div key={badge.label} className="flex items-center gap-2" data-testid={`trust-badge-${badge.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <badge.icon className={`w-5 h-5 ${badge.color}`} />
                  <span className="text-sm font-medium text-foreground">{badge.label}</span>
                </div>
              ))}
            </div>

            <form onSubmit={handleCheckout} className="space-y-6">
              {/* Customer Details */}
              <div className="bg-card p-6 rounded-3xl shadow-lg border border-border space-y-4">
                <h3 className="font-heading text-xl">Your Details</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name">Name *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="rounded-xl"
                      data-testid="checkout-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="customer_phone">Phone *</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      placeholder="+679 XXX XXXX"
                      required
                      className="rounded-xl"
                      data-testid="checkout-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_email">Email *</Label>
                  <Input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    placeholder="hello@example.com"
                    required
                    className="rounded-xl"
                    data-testid="checkout-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup_time">Preferred Pickup Time (optional)</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="pickup_time"
                      name="pickup_time"
                      type="time"
                      value={formData.pickup_time}
                      onChange={handleChange}
                      className="rounded-xl pl-10"
                      data-testid="checkout-time-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">We're open 7:00 AM - 6:00 PM</p>
                </div>

                {/* SMS Notifications */}
                <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="checkbox"
                    name="sms_notifications"
                    checked={formData.sms_notifications}
                    onChange={handleChange}
                    className="rounded"
                    data-testid="sms-notifications-checkbox"
                  />
                  <Bell className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">SMS Notifications</p>
                    <p className="text-xs text-muted-foreground">Get notified when your order is ready</p>
                  </div>
                </label>

                {/* Email Notifications */}
                <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.email_notifications}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_notifications: e.target.checked }))}
                    className="rounded"
                    data-testid="email-notifications-checkbox"
                  />
                  <Mail className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium">Email Updates</p>
                    <p className="text-xs text-muted-foreground">Receive order status updates via email</p>
                  </div>
                </label>

                {/* Browser Push Notifications */}
                {pushSupported && (
                  <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={pushEnabled || isPushSubscribed}
                      onChange={(e) => {
                        if (e.target.checked && pushPermission !== 'granted') {
                          subscribePush(user?.id).then(success => {
                            if (success) {
                              setPushEnabled(true);
                              toast.success('Push notifications enabled!');
                            } else {
                              toast.error('Please allow notifications in your browser');
                            }
                          });
                        } else {
                          setPushEnabled(e.target.checked);
                        }
                      }}
                      className="rounded"
                      data-testid="push-notifications-checkbox"
                    />
                    <BellRing className="w-5 h-5 text-violet-500" />
                    <div>
                      <p className="text-sm font-medium">Browser Notifications</p>
                      <p className="text-xs text-muted-foreground">Get instant browser alerts for order updates</p>
                    </div>
                  </label>
                )}
              </div>

              {/* Partner Referral Code - Only show if user doesn't have loyalty discount */}
              {loyaltyDiscount === 0 && (
                <div className="bg-card p-6 rounded-3xl shadow-lg border border-border space-y-4">
                  <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-teal-500" />
                    <h3 className="font-heading text-xl">Partner Referral Code</h3>
                    <span className="text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 px-2 py-0.5 rounded-full">Optional</span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    Got a code from a taxi driver or tour guide? Enter it for 5% off!
                  </p>
                  
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="e.g., BULA-XXXX"
                      value={partnerCode}
                      onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                      className={`rounded-xl pr-10 uppercase ${
                        partnerCodeValid === true ? 'border-green-500' :
                        partnerCodeValid === false ? 'border-red-500' : ''
                      }`}
                      data-testid="partner-code-input"
                    />
                    {validatingCode && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                    {!validatingCode && partnerCodeValid === true && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                    {!validatingCode && partnerCodeValid === false && (
                      <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                    )}
                  </div>
                  
                  {partnerCodeValid === true && partnerInfo && (
                    <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                        <span className="text-sm font-medium text-teal-700 dark:text-teal-300">
                          {partnerInfo.message}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {partnerCodeValid === false && partnerCode.length >= 4 && (
                    <p className="text-sm text-red-500">
                      Invalid code. Please check and try again.
                    </p>
                  )}
                </div>
              )}

              {/* Already have loyalty - inform user */}
              {loyaltyDiscount > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-4 rounded-2xl border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    <span className="font-medium text-amber-800 dark:text-amber-200">
                      {loyaltyTier?.name || 'Loyalty'} Discount Applied: {loyaltyDiscount}% off!
                    </span>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    As a loyalty member, you already get the best discount.
                  </p>
                </div>
              )}

              {/* Payment Method */}
              <div className="bg-card p-6 rounded-3xl shadow-lg border border-border space-y-4">
                <h3 className="font-heading text-xl">Payment Method</h3>
                
                <div className="space-y-3">
                  {/* M-PAiSA QR - Static QR Code (Same as POS) */}
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      formData.payment_method === 'mpaisa_qr' 
                        ? 'border-red-600 bg-red-600/10' 
                        : 'border-border hover:border-red-600/50'
                    }`}
                    data-testid="payment-mpaisa-qr"
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="mpaisa_qr"
                      checked={formData.payment_method === 'mpaisa_qr'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <QrCode className="w-6 h-6 text-red-600" />
                    <div className="flex-1">
                      <p className="font-medium">M-PAiSA QR Code</p>
                      <p className="text-sm text-muted-foreground">Scan QR code & enter confirmation code</p>
                    </div>
                    {formData.payment_method === 'mpaisa_qr' && (
                      <CheckCircle className="w-5 h-5 text-red-600" />
                    )}
                  </label>
                  
                  {/* M-PAiSA Link - Payment Link */}
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      formData.payment_method === 'mpaisa' 
                        ? 'border-red-500 bg-red-500/10' 
                        : 'border-border hover:border-red-500/50'
                    }`}
                    data-testid="payment-mpaisa"
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="mpaisa"
                      checked={formData.payment_method === 'mpaisa'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <Smartphone className="w-6 h-6 text-red-500" />
                    <div className="flex-1">
                      <p className="font-medium">M-PAiSA Payment Link</p>
                      <p className="text-sm text-muted-foreground">Get a payment link sent to your phone</p>
                    </div>
                    {formData.payment_method === 'mpaisa' && (
                      <CheckCircle className="w-5 h-5 text-red-500" />
                    )}
                  </label>

                  {/* Stripe */}
                  {settings?.payments?.stripe_enabled && (
                    <label 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.payment_method === 'stripe' 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="payment-stripe"
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="stripe"
                        checked={formData.payment_method === 'stripe'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <CreditCard className="w-6 h-6 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium">Credit/Debit Card</p>
                        <p className="text-sm text-muted-foreground">Pay securely online</p>
                      </div>
                      {formData.payment_method === 'stripe' && (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      )}
                    </label>
                  )}

                  {/* PayPal */}
                  {settings?.payments?.paypal_enabled && (
                    <label 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.payment_method === 'paypal' 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="payment-paypal"
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="paypal"
                        checked={formData.payment_method === 'paypal'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="w-6 h-6 bg-blue-700 rounded text-white text-xs font-bold flex items-center justify-center">PP</div>
                      <div className="flex-1">
                        <p className="font-medium">PayPal</p>
                        <p className="text-sm text-muted-foreground">Pay with your PayPal account</p>
                      </div>
                      {formData.payment_method === 'paypal' && (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      )}
                    </label>
                  )}
                </div>

                {/* M-PAiSA QR Code Section */}
                {formData.payment_method === 'mpaisa_qr' && (
                  <div className="p-6 bg-red-50 dark:bg-red-950/20 rounded-xl border border-red-200 dark:border-red-800">
                    <div className="text-center">
                      <h4 className="font-medium text-red-800 dark:text-red-300 mb-4">Scan & Pay with M-PAiSA</h4>
                      
                      {/* QR Code Image */}
                      <div className="flex justify-center mb-4">
                        <div className="bg-white p-3 rounded-xl border-2 border-red-300 dark:border-red-700">
                          <img 
                            src="/mpaisa-qr-code.png" 
                            alt="M-PAiSA QR Code" 
                            className="w-32 h-32"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=mpaisa://pay/9290013';
                            }}
                          />
                        </div>
                      </div>
                      
                      <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                        1. Scan the QR code with your M-PAiSA app<br/>
                        2. Pay <span className="font-bold">${finalTotal.toFixed(2)}</span><br/>
                        3. Enter your confirmation code below
                      </p>
                      
                      {/* Reference Code Input */}
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={12}
                        placeholder="Enter confirmation code"
                        name="mpaisa_ref_code"
                        value={formData.mpaisa_ref_code || ''}
                        onChange={handleChange}
                        className="max-w-xs mx-auto text-center font-mono text-lg tracking-widest border-red-300 dark:border-red-700"
                      />
                      {formData.mpaisa_ref_code && formData.mpaisa_ref_code.length < 6 && (
                        <p className="text-xs text-red-500 mt-1">Code must be at least 6 digits</p>
                      )}
                    </div>
                  </div>
                )}

                {/* M-PAiSA Link Instructions */}
                {formData.payment_method === 'mpaisa' && (
                  <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-xl border border-red-200 dark:border-red-800">
                    <h4 className="font-medium text-red-800 dark:text-red-300 mb-2">M-PAiSA Payment Link</h4>
                    <p className="text-sm text-red-700 dark:text-red-400">
                      After placing your order, you'll receive a secure payment link.
                      Click the link to complete payment through your M-PAiSA app.
                    </p>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="bg-muted/30 p-6 rounded-3xl">
                <h3 className="font-heading text-xl mb-4">Order Summary</h3>
                <div className="space-y-3 mb-4">
                  {cart.map(item => {
                    const checkoutDetails = getCustomizationDetails(item);
                    return (
                      <div key={item.cart_key} className="flex justify-between text-sm pb-3 border-b border-border/50 last:border-0 last:pb-0">
                        <div className="flex-1 pr-4">
                          <span className="font-medium text-foreground">{item.name} x{item.quantity}</span>
                          {getItemDescription(item) && (
                            <p className="text-xs text-muted-foreground mt-0.5">{getItemDescription(item)}</p>
                          )}
                          {checkoutDetails.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {checkoutDetails.map((detail) => (
                                detail.items.map((itemText) => (
                                  <span 
                                    key={`${detail.type}-${itemText}`}
                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                      detail.type === 'removed' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                      detail.type === 'extra' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                                      'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                    }`}
                                  >
                                    {detail.type === 'removed' ? 'No ' : detail.type === 'extra' ? '+' : '+'}{itemText}
                                  </span>
                                ))
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-foreground">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between font-heading text-xl pt-4 border-t border-border">
                  <span>Subtotal</span>
                  <span>{cartTotal.toFixed(2)}</span>
                </div>
                {effectiveDiscount > 0 && (
                  <div className="flex justify-between text-sm pt-2">
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      {loyaltyDiscount > 0 ? (
                        <>
                          <Star className="w-4 h-4" />
                          {loyaltyTier?.name || 'Loyalty'} ({effectiveDiscount}%)
                        </>
                      ) : (
                        <>
                          <Tag className="w-4 h-4" />
                          Partner Discount ({effectiveDiscount}%)
                        </>
                      )}
                    </span>
                    <span className="text-green-600 dark:text-green-400">-{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {hasCoffeeClubPass && (
                  <div className="flex justify-between text-sm pt-2">
                    <span className="text-muted-foreground">VAT (12.5% on Coffee Pass)</span>
                    <span className="text-muted-foreground">+${coffeePassVAT.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-heading text-2xl pt-2 mt-2 border-t border-border">
                  <span>Total</span>
                  <span className="text-primary">{finalTotal.toFixed(2)}</span>
                </div>
              </div>

              <Button 
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-primary hover:bg-primary/90 py-6"
                data-testid="place-order-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Placing Order...
                  </>
                ) : (
                  `Place Order • ${finalTotal.toFixed(2)}`
                )}
              </Button>
              
              {/* Location Disclaimer */}
              <LocationDisclaimer className="mt-4" />
            </form>
          </div>
        </section>
      </div>
    );
  }

  // Confirmation View
  if (step === 'confirmation') {
    return (
      <div className="pt-24 pb-16 min-h-screen" data-testid="order-confirmation-page">
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6"
              data-testid="order-success-icon"
            >
              <CheckCircle className="w-10 h-10 text-primary" />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-heading text-4xl mb-4"
              data-testid="order-confirmed-title"
            >
              Order Confirmed!
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-muted-foreground mb-8"
            >
              Thank you for your order. We're preparing your delicious items!
            </motion.p>

            {orderDetails && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card p-6 rounded-3xl shadow-lg border border-border text-left mb-8"
              >
                <h3 className="font-heading text-xl mb-4">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-muted-foreground">Order ID:</span> {orderDetails.id}</p>
                  <p><span className="text-muted-foreground">Total:</span> {orderDetails.total.toFixed(2)}</p>
                  <p><span className="text-muted-foreground">Payment:</span> {orderDetails.payment_method === 'counter' ? 'Pay at Counter' : orderDetails.payment_method.toUpperCase()}</p>
                  {orderDetails.estimated_ready_time && (
                    <p><span className="text-muted-foreground">Estimated Ready:</span> {orderDetails.estimated_ready_time}</p>
                  )}
                  {orderDetails.pickup_time && (
                    <p><span className="text-muted-foreground">Pickup Time:</span> {orderDetails.pickup_time}</p>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="font-medium mb-2">Items:</p>
                  {orderDetails.items.map((item) => (
                    <div key={item.id || `${item.name}-${item.quantity}`} className="text-sm text-muted-foreground">
                      <p>{item.name} x{item.quantity}</p>
                      {item.selected_size && <p className="text-xs ml-2">Size: {item.selected_size}</p>}
                    </div>
                  ))}
                </div>

                {/* M-PAiSA Reminder */}
                {orderDetails.payment_method === 'mpaisa' && (
                  <div className="mt-4 p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-green-800 font-medium">
                      Don't forget to complete your M-PAiSA transfer and show the confirmation at pickup!
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Coffee Pass Upsell - Show to non-members after order */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-8"
            >
              <CoffeePassOrderUpsell />
            </motion.div>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/menu" data-testid="order-again-btn">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Order More
                </Button>
              </Link>
              <Link to="/" data-testid="back-home-btn">
                <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary">
                  Back to Home
                </Button>
              </Link>
            </div>
            
            {/* Google Review Prompt */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-8 p-6 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-800"
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              </div>
              <h3 className="font-heading text-lg text-amber-800 dark:text-amber-200 mb-2">
                Enjoying Bulaccino?
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                We'd love to hear your feedback! Leave us a review on Google.
              </p>
              <a
                href="https://g.page/r/CbYHsfEsqm9iEAI/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-medium transition-colors"
                data-testid="order-google-review-btn"
              >
                <Star className="w-4 h-4 fill-white" />
                Leave a Review
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return null;
}
